import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import t from 'react-translate';
import { InfiniteTableLoadingParams, PagedDataQueryParams } from 'redux/create-action-creators';
import { LearnerProgress, LearnerProgressSearch } from 'redux/schemas/models/learner-progress';
import { Result } from 'redux/schemas/api';
import { LearnerProgressSavedView, SavedViewFilters } from 'redux/schemas/models/learner-progress-saved-view';
import { CommunicationFilter, CommunicationType } from 'redux/schemas/models/course-communication';
import { addAlertMessage } from './alert-messages';
import { AlertMessageType } from '../schemas/app/alert-message';
import { makeQueryParams, makeQueryParamString } from './helpers';


type LearnerProgressParams = {
  catalogId: string,
  translationValues?: string,
  sectionIds?: number[]
};

export const getLearnerProgressCounts = createAsyncThunk(
  'GET_LEARNER_PROGRESS_COUNTS',
  async (params: LearnerProgressParams) => axios.get(`${params.catalogId}/learner_progress/stats`).then(response => response.data.result),
);

type LearnerProgressListPageParams = LearnerProgressParams & PagedDataQueryParams & InfiniteTableLoadingParams<LearnerProgressSearch>;

export const getLearnerProgressList = createAsyncThunk<{ response: LearnerProgress[], totalCount: number | null }, any, {}>(
  'GET_LEARNER_PROGRESS_LIST',
  async (params: LearnerProgressListPageParams) => {
    const queryParams = makeQueryParams(params, params.pageSize, params.pageIndex);
    const paramString = makeQueryParamString(queryParams);

    return axios.get<Result<LearnerProgressSearch>>(`${params.catalogId}/learner_progress?${paramString}`, {
      params: {
        section_ids: params.sectionIds,
      },
    }).then((response) => ({
      response: response.data.result.result,
      // resultCount is only reliably computed by the backend alongside an active text search; null otherwise.
      totalCount: response.data.result.resultCount ?? null,
    }));
  },
);

export const getCourseOutlineSections = createAsyncThunk(
  'GET_COURSE_OUTLINE_SECTIONS',
  async (params: LearnerProgressParams) => axios.get(`${params.catalogId}/course_outline/content`).then(response => response.data.result),
);

type LearnerEmailsParams = LearnerProgressParams & { filters?: PagedDataQueryParams['filters'] };

export const getLearnerEmails = createAsyncThunk<string[], LearnerEmailsParams>(
  'GET_LEARNER_EMAILS',
  async (params) => axios.get(`${params.catalogId}/learner_progress/learners`, {
    params: { fields: 'email', ...params.filters },
  }).then(response => response.data.result.learners.map((learner: { email: string }) => learner.email)),
);

type LearnerProgressIndividualActionParams = LearnerProgressParams & { userId: number };

export const addManualCompletion = createAsyncThunk(
  'ADD_MANUAL_COMPLETION',
  async (params: LearnerProgressIndividualActionParams, thunkAPI) => axios.put(`${params.catalogId}/manual_completion/${params.userId}`).then(response => {
    thunkAPI.dispatch(addAlertMessage({
      type: AlertMessageType.SUCCESS,
      header: t.FORM.SUCCESS(),
      message: t.LEARNER_PROGRESS.OPTIONS.GRANT_MANUAL_COMPLETION_SUCCESS(params.translationValues),
    }));

    return response.data.result;
  }),
);

export const removeManualCompletion = createAsyncThunk(
  'REVOKE_MANUAL_COMPLETION',
  async (params: LearnerProgressIndividualActionParams, thunkAPI) => axios.delete(`${params.catalogId}/manual_completion/${params.userId}`).then(response => {
    thunkAPI.dispatch(addAlertMessage({
      type: AlertMessageType.SUCCESS,
      header: t.FORM.SUCCESS(),
      message: t.LEARNER_PROGRESS.OPTIONS.REVOKE_MANUAL_COMPLETION_SUCCESS(params.translationValues),
    }));

    return response.data.result;
  }),
);

export const getManuallyAwardedPoints = createAsyncThunk(
  'GET_MANUALLY_AWARDED_POINTS',
  async (params: LearnerProgressParams) => axios.get(`${params.catalogId}/awarded_points`).then(response => response.data.result),
);

// Saved Views CRUD

export const getSavedViews = createAsyncThunk<LearnerProgressSavedView[], { catalogId: string }>(
  'GET_LEARNER_PROGRESS_SAVED_VIEWS',
  async (params) => axios.get(`${params.catalogId}/admin_custom_views`).then(res => res.data.result),
);

export const createSavedView = createAsyncThunk<LearnerProgressSavedView, { catalogId: string; viewName: string; filters: SavedViewFilters }>(
  'CREATE_LEARNER_PROGRESS_SAVED_VIEW',
  async (params) => axios.post(`${params.catalogId}/admin_custom_views`, {
    viewName: params.viewName,
    filters: params.filters,
  }).then(res => res.data.result),
);

export const updateSavedView = createAsyncThunk<LearnerProgressSavedView, { catalogId: string; id: number; viewName?: string; filters?: SavedViewFilters }>(
  'UPDATE_LEARNER_PROGRESS_SAVED_VIEW',
  async (params) => axios.put(`${params.catalogId}/admin_custom_views/${params.id}`, {
    viewName: params.viewName,
    filters: params.filters,
  }).then(res => res.data.result),
);

export const deleteSavedView = createAsyncThunk<number, { catalogId: string; id: number; viewName: string }>(
  'DELETE_LEARNER_PROGRESS_SAVED_VIEW',
  async (params, thunkAPI) => {
    await axios.delete(`${params.catalogId}/admin_custom_views/${params.id}`);
    thunkAPI.dispatch(addAlertMessage({
      type: AlertMessageType.SUCCESS,
      header: t.LEARNER_PROGRESS.SAVED_VIEWS.DELETE_SUCCESS_HEADER(params.viewName),
      message: t.LEARNER_PROGRESS.SAVED_VIEWS.DELETE_SUCCESS_MESSAGE(),
    }));
    return params.id;
  },
);

type SendLearnerProgressEmailParams = {
  catalogId: string;
  communication: { subject: string; body: string };
  filters?: CommunicationFilter;
  enablePushNotification?: boolean;
  pushNotificationBody?: string;
};

export const sendLearnerProgressEmail = createAsyncThunk(
  'SEND_LEARNER_PROGRESS_EMAIL',
  async (params: SendLearnerProgressEmailParams, thunkAPI) => {
    try {
      const response = await axios.post(`${params.catalogId}/course_communications.json`, {
        communicationType: CommunicationType.LEARNER_PROGRESS_EMAIL,
        submitted: true,
        recipients: ['students'],
        communication: params.communication,
        enablePushNotification: params.enablePushNotification ?? false,
        ...(params.pushNotificationBody ? { pushNotificationBody: params.pushNotificationBody } : {}),
        ...(params.filters ? { filters: params.filters } : {}),
      });
      thunkAPI.dispatch(addAlertMessage({
        type: AlertMessageType.SUCCESS,
        header: t.FORM.SUCCESS_BANG(),
        message: t.LEARNER_PROGRESS.CONTACT_BAR.SEND_SUCCESS(),
      }));
      return response.data.result;
    } catch (error) {
      thunkAPI.dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.ERROR(),
        message: t.LEARNER_PROGRESS.CONTACT_BAR.SEND_ERROR(),
      }));
      return thunkAPI.rejectWithValue(error);
    }
  },
);

type SendTestLearnerProgressEmailParams = {
  catalogId: string;
  communication: {
    subject: string;
    body: string;
  };
  enablePushNotification?: boolean;
  pushNotificationBody?: string;
};

export const sendTestLearnerProgressEmail = createAsyncThunk(
  'SEND_TEST_LEARNER_PROGRESS_EMAIL',
  async (params: SendTestLearnerProgressEmailParams, thunkAPI) => {
    try {
      const response = await axios.post(`${params.catalogId}/course_communications/send_test_email.json`, {
        communicationType: CommunicationType.LEARNER_PROGRESS_EMAIL,
        recipients: ['students'],
        communication: params.communication,
        enablePushNotification: params.enablePushNotification ?? false,
        ...(params.pushNotificationBody ? { pushNotificationBody: params.pushNotificationBody } : {}),
      });
      thunkAPI.dispatch(addAlertMessage({
        type: AlertMessageType.SUCCESS,
        header: t.FORM.SUCCESS_BANG(),
        message: t.LEARNER_PROGRESS.CONTACT_BAR.SEND_TEST_SUCCESS(),
      }));
      return response.data.result;
    } catch (error) {
      thunkAPI.dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.ERROR(),
      }));
      return thunkAPI.rejectWithValue(error);
    }
  },
);
